import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from 'react-query'
import { futureProductsAPI } from '../services/api'
import { X } from 'lucide-react'
import toast from 'react-hot-toast'
import ImageUpload from './ImageUpload'

export default function FutureProductModal({ product, onClose, onSuccess }) {
  const [selectedImage, setSelectedImage] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const queryClient = useQueryClient()

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      display_order: product?.display_order || 0,
    }
  })

  useEffect(() => {
    if (product) {
      setValue('display_order', product.display_order || 0)
    }
  }, [product, setValue])

  const mutation = useMutation(
    async (formData) => {
      if (product) {
        return futureProductsAPI.update(product.uuid, formData)
      } else {
        return futureProductsAPI.create(formData)
      }
    },
    {
      onSuccess: () => {
        toast.success(product ? 'Future product updated successfully!' : 'Future product created successfully!')
        queryClient.invalidateQueries('futureProducts')
        onSuccess()
      },
      onError: (error) => {
        console.error('Mutation error:', error)
        toast.error(error.response?.data?.message || 'Operation failed')
      }
    }
  )

  const onSubmit = async (data) => {
    setIsUploading(true)
    try {
      if (!selectedImage && !product?.image_url) {
        toast.error('Please select an image')
        setIsUploading(false)
        return
      }

      const formData = new FormData()
      formData.append('display_order', data.display_order || 0)
      
      if (selectedImage) {
        formData.append('image', selectedImage)
      }
      
      mutation.mutate(formData)
    } catch (error) {
      console.error('Submission error:', error)
      toast.error('Failed to save future product')
    } finally {
      setIsUploading(false)
    }
  }

  const handleImageChange = (file) => {
    setSelectedImage(file)
  }

  return (
    <div className="fixed inset-0 bg-black/70 overflow-y-auto h-full w-full z-50 flex items-center justify-center px-4 py-6 md:py-8">
      <div className="relative w-full max-w-2xl mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 animate-scale-in max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-5 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            {product ? 'Edit Future Product' : 'Add New Future Product'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Close"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col h-full">
          <div className="p-5 space-y-6 overflow-y-auto flex-grow">
            {/* Display Order */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Display Order
              </label>
              <input
                type="number"
                {...register('display_order', { valueAsNumber: true })}
                className="block w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                placeholder="Display order (0 = first)"
              />
            </div>

            {/* Image Upload */}
            <ImageUpload
              value={product?.image_url}
              onChange={handleImageChange}
              label="Product Image"
              required={true}
              error={!selectedImage && !product?.image_url ? 'Image is required' : ''}
            />
          </div>

          <div className="flex justify-end space-x-3 p-5 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isUploading || mutation.isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 disabled:cursor-not-allowed rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200"
            >
              {isUploading || mutation.isLoading ? 'Saving...' : (product ? 'Update Product' : 'Create Product')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
